/**
 * The Advisor list's state: scope (own runs, or everyone's with `advisor.view.all`), status, search and page live in
 * the URL, so a link reopens the same view; the search is debounced before it reaches the URL.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAdvisorRuns } from '@/api/queries';
import type { AdvisorRunListParams, AdvisorRunStatus } from '@/api/types';
import { useSession } from '@/session/SessionContext';
import { useDebouncedSearch } from '@/ui/useDebouncedSearch';

import { STATUS_LABELS } from './advisorUi';

const PAGE_SIZES = [25, 50, 100];

export function readParams(search: URLSearchParams, canAll: boolean): AdvisorRunListParams {
  const status = search.get('status');
  const page = Number(search.get('page'));
  const size = Number(search.get('size'));
  return {
    scope: canAll && search.get('scope') === 'all' ? 'all' : 'mine',
    status: status && status in STATUS_LABELS ? (status as AdvisorRunStatus) : null,
    q: search.get('q') ?? '',
    page: Number.isInteger(page) && page > 0 ? page : 1,
    page_size: PAGE_SIZES.includes(size) ? size : 25,
  };
}

export function writeParams(params: AdvisorRunListParams): URLSearchParams {
  const out = new URLSearchParams();
  if (params.scope === 'all') out.set('scope', 'all');
  if (params.status) out.set('status', params.status);
  if (params.q) out.set('q', params.q);
  if (params.page > 1) out.set('page', String(params.page));
  if (params.page_size !== 25) out.set('size', String(params.page_size));
  return out;
}

export function useAdvisorList() {
  const { can } = useSession();
  const canAll = can('advisor.view.all');
  const [search, setSearch] = useSearchParams();
  const params = readParams(search, canAll);
  const update = (patch: Partial<AdvisorRunListParams>) =>
    setSearch(writeParams({ ...params, page: 1, ...patch }), { replace: true });
  const [text, setText] = useDebouncedSearch(params.q, ({ q }) => update({ q }));
  const list = useAdvisorRuns(params);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  return {
    params,
    update,
    text,
    setText,
    list,
    canAll,
    canRun: can('advisor.run'),
    canDeleteClient: can('advisor.client.delete'),
    filtered: params.q !== '' || params.status !== null,
    creating,
    setCreating,
    deleting,
    setDeleting,
  };
}

export type AdvisorListState = ReturnType<typeof useAdvisorList>;
